import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { listShareLinks, revokeShareLink, updateShareLink } from '../../api/shareLinkApi';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Field';
import { Modal } from '../../components/ui/Modal';
import {
  CellActions,
  CellMono,
  CellPrimary,
  CellSub,
  Table,
  TableEmpty,
  TableScroll,
  Tbody,
  Td,
  Th,
  Thead,
  Tr
} from '../../components/ui/Table';
import { formatDateTime } from '../../lib/format';
import { keys } from '../../lib/queryKeys';
import { useConfirm } from '../../providers/ConfirmProvider';
import { useToast } from '../../providers/ToastProvider';
import { StatusBadge } from './ShareLinkModal';
import {
  SHARE_LINK_STATUS,
  fromDateTimeLocalValue,
  minimumExpiryInputValue,
  toDateTimeLocalValue
} from './shareTemplate';

const COLUMNS = 6;

/**
 * The operator's previously generated links: status, expiry, usage, and revoke.
 *
 * URLs are not listed because only a hash of each token is stored; the last four
 * characters identify which link is which.
 */
export function ManageShareLinksModal({ open, onClose }) {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: keys.shareLinks,
    queryFn: ({ signal }) => listShareLinks(signal),
    enabled: open,
    staleTime: 10_000
  });

  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [busyId, setBusyId] = useState(null);

  const links = Array.isArray(query.data?.links) ? query.data.links : [];
  const minExpiry = minimumExpiryInputValue();
  const hasLinks = links.length > 0;

  const refresh = () => queryClient.invalidateQueries({ queryKey: keys.shareLinks });

  function startEdit(link) {
    setEditingId(link.id);
    setEditValue(toDateTimeLocalValue(link.expiresAt));
  }

  async function saveExpiry(link) {
    setBusyId(link.id);
    try {
      await updateShareLink(link.id, { expiresAt: fromDateTimeLocalValue(editValue) });
      setEditingId(null);
      toast.success('Expiry updated.');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Could not update the expiry.');
    } finally {
      setBusyId(null);
    }
  }

  async function revoke(link) {
    const ok = await confirm({
      title: 'Revoke this link?',
      message: [
        `“${link.name || 'Untitled'}” (…${link.tokenHint}) will stop opening for everyone.`,
        'Forms already opened from it are unaffected. This cannot be undone.'
      ],
      confirmLabel: 'Revoke Link',
      cancelLabel: 'Keep Link',
      tone: 'danger'
    });
    if (!ok) return;

    setBusyId(link.id);
    try {
      await revokeShareLink(link.id);
      toast.success('Link revoked.');
      await refresh();
    } catch (err) {
      toast.error(err.message || 'Could not revoke the link.');
    } finally {
      setBusyId(null);
    }
  }

  // Footer mirrors the Share dialog: a muted summary pinned left, the primary action
  // right, so the two dialogs share one footer rhythm.
  const footer = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <span className="text-xs text-muted">
        {hasLinks ? `${links.length} link${links.length === 1 ? '' : 's'}` : ''}
      </span>
      <Button variant="primary" onClick={onClose}>
        Done
      </Button>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title="Shared form links" titleIcon="fa-link" size="xl" footer={footer}>
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-relaxed text-muted">
          Links you have generated from the campaign composer. Full URLs are shown only once, when a link is
          created — change a link&apos;s expiry or revoke it to stop it working.
        </p>

        <TableScroll maxHeight="58vh">
          <Table>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>Created</Th>
                <Th>Expires</Th>
                <Th>Status</Th>
                <Th align="right">Opens</Th>
                <Th align="right">Actions</Th>
              </tr>
            </Thead>
            <Tbody>
              {query.isLoading ? (
                <TableEmpty colSpan={COLUMNS} loading title="Loading links…" />
              ) : query.error ? (
                <TableEmpty colSpan={COLUMNS} icon="fa-triangle-exclamation" title="Could not load links">
                  {query.error.message}
                </TableEmpty>
              ) : links.length === 0 ? (
                <TableEmpty colSpan={COLUMNS} icon="fa-link" title="No shared links yet">
                  Use Generate Link in the composer to create one.
                </TableEmpty>
              ) : (
                links.map((link) => {
                  const isRevoked = link.status === SHARE_LINK_STATUS.REVOKED;
                  const isEditing = editingId === link.id;
                  const isBusy = busyId === link.id;

                  return (
                    <Tr key={link.id}>
                      <Td>
                        <CellPrimary>{link.name || 'Untitled'}</CellPrimary>
                        <CellSub>
                          <CellMono>…{link.tokenHint}</CellMono>
                        </CellSub>
                      </Td>
                      <Td className="whitespace-nowrap">{formatDateTime(link.createdAt)}</Td>
                      <Td>
                        {isEditing ? (
                          <Input
                            type="datetime-local"
                            aria-label="New expiry"
                            value={editValue}
                            min={minExpiry}
                            onChange={(event) => setEditValue(event.target.value)}
                          />
                        ) : link.expiresAt ? (
                          <span className="whitespace-nowrap">{formatDateTime(link.expiresAt)}</span>
                        ) : (
                          <span className="text-muted">Never</span>
                        )}
                      </Td>
                      <Td>
                        <StatusBadge status={link.status} />
                      </Td>
                      <Td align="right">
                        <span className="font-semibold text-ink-800 tabular-nums">{link.openCount}</span>
                        {link.lastOpenedAt ? (
                          <CellSub className="whitespace-nowrap">last {formatDateTime(link.lastOpenedAt)}</CellSub>
                        ) : null}
                      </Td>
                      <Td align="right">
                        <CellActions className="justify-end">
                          {isRevoked ? (
                            <span className="text-xs text-muted">—</span>
                          ) : isEditing ? (
                            <>
                              <Button size="sm" variant="primary" onClick={() => saveExpiry(link)} loading={isBusy}>
                                Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} disabled={isBusy}>
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                icon="fa-clock"
                                onClick={() => startEdit(link)}
                                disabled={isBusy}
                              >
                                Expiry
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                icon="fa-ban"
                                onClick={() => revoke(link)}
                                loading={isBusy}
                              >
                                Revoke
                              </Button>
                            </>
                          )}
                        </CellActions>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </TableScroll>
      </div>
    </Modal>
  );
}
